import { readCheckScope, submissionKey } from '../../../../features/checks/checkScope';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getLocalTodayString } from '../../../../components/CreationDate';
import { fetchChecklistBySlug, fetchSubmissionByDate, submitChecklistData, submitTemperatureResults } from '../../../../features/checks/checkActions';
import { getUsersAsync } from '../../../../features/users/usersActions';
import { selectStaffList, selectUsersLoading, selectUsersError } from '../../../../features/users/usersSlice';

const FoodTemperatureContext = createContext(null);
const SLUG = 'temperature-check';
const EMPTY_LOGS = [];
const emptyEntry = () => ({
    time: '', foodItemName: '', type: 'Hot Holding', initialTemp: '', finalTemp: '',
    actionTaken: '', comments: '', photo: null,
});
const isCalendarDate = value => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
    const date = new Date(`${value}T00:00:00`);
    return !Number.isNaN(date.getTime()) &&
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` === value;
};

export function FoodTemperatureProvider({ children }) {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const dateParam = searchParams.get('date');
    const selectedDate = isCalendarDate(dateParam) ? dateParam : getLocalTodayString();
    const checklist = useSelector(state => state.checks.checklistsBySlug[SLUG]);
    const record = useSelector(state => state.checks.submissionsByDate[submissionKey(checklist?.id, selectedDate, readCheckScope().locationCode)]);
    const staff = useSelector(selectStaffList);
    const staffLoading = useSelector(selectUsersLoading);
    const staffError = useSelector(selectUsersError);
    const [templateLoading, setTemplateLoading] = useState(true);
    const [templateError, setTemplateError] = useState(null);
    const [entry, setEntry] = useState(emptyEntry);
    const [isSaving, setIsSaving] = useState(false);
    const [operationError, setOperationError] = useState(null);
    const submission = record?.data || null;
    const logs = submission?.temperatureSubmissions || EMPTY_LOGS;
    const selectedUser = submission?.completedBy || null;
    const loading = templateLoading || Boolean(record?.loading);
    const error = templateError || record?.error || operationError;
    const formattedDate = new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
    const isToday = selectedDate === getLocalTodayString();

    useEffect(() => {
        let active = true;
        dispatch(fetchChecklistBySlug(SLUG)).unwrap()
            .catch(err => { if (active) setTemplateError(err); })
            .finally(() => { if (active) setTemplateLoading(false); });
        dispatch(getUsersAsync('STAFF'));
        return () => { active = false; };
    }, [dispatch]);

    useEffect(() => {
        if (!checklist?.id) return;
        const request = dispatch(fetchSubmissionByDate({ checklistId: checklist.id, creationDate: selectedDate }));
        return () => request.abort();
    }, [dispatch, checklist?.id, selectedDate]);

    useEffect(() => {
        setEntry(emptyEntry());
        setOperationError(null);
    }, [selectedDate]);

    const setSelectedDate = useCallback(date => {
        if (!isCalendarDate(date) || isSaving) return;
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            next.set('date', date);
            return next;
        });
    }, [isSaving, setSearchParams]);
    const updateEntry = useCallback((field, value) => {
        setEntry(previous => ({ ...previous, [field]: value }));
    }, []);
    const clearEntry = useCallback(() => {
        setEntry(emptyEntry());
        setOperationError(null);
    }, []);
    const refreshSubmission = useCallback(() => dispatch(fetchSubmissionByDate({
        checklistId: checklist.id, creationDate: selectedDate,
    })).unwrap(), [dispatch, checklist, selectedDate]);

    const selectUser = async user => {
        if (!checklist?.id || loading || isSaving) return;
        setIsSaving(true);
        setOperationError(null);
        const data = new FormData();
        data.append('checklistId', checklist.id);
        data.append('completedById', user.id);
        data.append('creationDate', selectedDate);
        try {
            await dispatch(submitChecklistData(data)).unwrap();
        } catch (err) {
            setOperationError(err || 'Failed to save staff member.');
        } finally {
            setIsSaving(false);
        }
    };
    const addEntry = async () => {
        if (!submission?.id || loading || isSaving) {
            setOperationError('Please select a staff member before adding an entry.');
            return false;
        }
        setIsSaving(true);
        setOperationError(null);
        const data = new FormData();
        data.append('submissionId', submission.id);
        data.append('date', selectedDate);
        Object.entries(entry).forEach(([field, value]) => {
            if (field !== 'photo' || value) data.append(field, value);
        });
        try {
            await dispatch(submitTemperatureResults(data)).unwrap();
        } catch (err) {
            setOperationError(err || 'Failed to save temperature entry.');
            setIsSaving(false);
            return false;
        }
        clearEntry();
        try {
            await refreshSubmission();
        } catch (err) {
            setOperationError('Entry saved, but logs could not be refreshed. Please reload the page.');
        } finally {
            setIsSaving(false);
        }
        return true;
    };
    const stats = useMemo(() => {
        const withinRange = logs.filter(log => log.status === 'Within Range').length;
        const outOfRange = logs.filter(log => log.status === 'Out of Range').length;
        return { total: logs.length, withinRange, outOfRange, unassessed: logs.length - withinRange - outOfRange };
    }, [logs]);

    return (
        <FoodTemperatureContext.Provider value={{
            selectedDate, setSelectedDate, formattedDate, isToday, checklist, submission, logs,
            loading, error, staff, staffLoading, staffError, selectedUser, selectUser,
            entry, updateEntry, clearEntry, addEntry, isSaving, stats,
        }}>
            {children}
        </FoodTemperatureContext.Provider>
    );
}

export function useFoodTemperature() {
    const context = useContext(FoodTemperatureContext);
    if (!context) throw new Error('useFoodTemperature must be used inside FoodTemperatureProvider');
    return context;
}
